/**
 * Telling the host apart from the people they invited.
 *
 * The participants list in a scheduled meeting's details is built from
 * `meeting.invitees`, and that list INCLUDES the host — the backend keeps it as
 * the full participant set, not as "everyone except the organiser". Every row
 * was then labelled the same way, so the person who created the meeting was
 * shown as "Invited", alongside the people they had actually invited.
 *
 * ## Why this tries three identifiers instead of one
 *
 * The obvious check is `contact.id === meeting.hostId`, and on its own it is
 * unreliable here: `invitees` are CONTACT ids from the address book, while
 * `hostId` is a user id. The two are different id spaces, so that comparison can
 * be false for the host and never true for anyone — a silent no-op that looks
 * exactly like the bug it was meant to fix.
 *
 * So each identifier is tried in turn and any match settles it, rather than the
 * strongest available one deciding alone. A meeting record may carry a host id,
 * a host email, a host name, or some combination, depending on where it came
 * from — the scheduling form, the API, or a legacy record rehydrated from
 * storage.
 *
 * ## Which way to be wrong
 *
 * A false positive labels an invitee as the host, which misattributes the
 * meeting. A false negative leaves the host reading "Invited", which is the
 * complaint this fixes and is merely untidy. The first is worse, so every
 * comparison is EQUALITY after trimming and case-folding — never a substring
 * test. The `joined` check a few lines away in the same list matches attendee
 * strings with `includes` on a first name, which is exactly the looseness being
 * avoided: two people called John would both be marked joined.
 */

export type HostIdentity = {
  hostId?: string | null
  hostEmail?: string | null
  /** Display name, which is all some records carry. */
  host?: string | null
}

export type ParticipantIdentity = {
  id?: string | null
  email?: string | null
  name?: string | null
}

/** Case-insensitive, whitespace-tolerant equality. Empty never matches empty. */
function same(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  const x = a.trim().toLowerCase()
  const y = b.trim().toLowerCase()
  return x !== '' && x === y
}

/**
 * Is this participant the meeting's host?
 *
 * Email is checked before name because it is the identifier people do not
 * share; two colleagues can be "Alex Morgan" and only one can hold an address.
 */
export function isMeetingHost(
  participant: ParticipantIdentity | null | undefined,
  meeting: HostIdentity | null | undefined,
): boolean {
  if (!participant || !meeting) return false
  if (same(participant.id, meeting.hostId)) return true
  if (same(participant.email, meeting.hostEmail)) return true
  return same(participant.name, meeting.host)
}
